import {Navigate, Route, Routes, useLocation} from "react-router-dom";

import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Projects from "./components/Projects";
import About from "./components/About";
import Services from "./components/Services";
import Portfolio from "./components/Portfolio";
import Contacts from "./components/Contacts";
import Footer from "./components/Footer";
import Home from "./components/Home";
import Testimonials from "./components/Testimonials";
import SEO from "./components/SEO";
import CookieBanner from "./components/CookieBanner";
import WhatsAppButton from "./components/WhatsAppButton";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";

import useLanguage from "./hooks/useLanguage";
import {I18nProvider, useT} from "./i18n/i18n";

const routeConfig = (language) => [
    {
        path: "/about",
        component: <About language={language}/>,
        seoKey: "seo.about",
    },
    {
        path: "/services",
        component: <Services language={language}/>,
        seoKey: "seo.services",
    },
    {
        path: "/portfolio",
        component: <Portfolio language={language}/>,
        seoKey: "seo.portfolio",
    },
    {
        path: "/projects",
        component: <Projects language={language}/>,
        seoKey: "seo.projects",
    },
    {
        path: "/contacts",
        component: <Contacts language={language}/>,
        seoKey: "seo.contacts",
    },
];

function App() {
    const [language, setLanguage] = useLanguage();
    const {tObject} = useT(language);
    const location = useLocation();
    
    // Check if current page is home page
    const isHomePage = location.pathname === "/" || location.pathname === "/home";

    return (
        <I18nProvider locale={language} setLocale={setLanguage}>
            <div
                className={`relative w-full min-h-screen flex flex-col transition-colors overflow-x-hidden ${
                    isHomePage 
                        ? "text-[rgb(var(--app-fg))] dark" 
                        : "text-black"
                }`}
                style={{ backgroundColor: "rgb(var(--app-bg))" }}
            >
                {/* Dark mode permanent */}
                <div className="absolute inset-0 -z-20" style={{ backgroundColor: "rgb(var(--app-bg))" }}></div>
                
                {isHomePage ? (
                    <>
                        {/* Home page with hero background */}
                        <div className="relative w-full">
                            <Hero language={language}/>
                            {/* Navbar overlay on hero */}
                            <div className="absolute top-0 left-0 right-0 z-50 w-full">
                                <div className="max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                    <Navbar language={language} setLanguage={setLanguage} isHomePage={true}/>
                                </div>
                            </div>
                        </div>
                        <main className="flex-grow text-[16px] sm:text-[17px] leading-[1.65] w-full relative z-10">
                            <Routes>
                                <Route 
                                    path="/" 
                                    element={
                                        <>
                                            <SEO {...tObject("seo.home")} />
                                            <Home language={language} />
                                        </>
                                    }
                                />
                                <Route 
                                    path="/home" 
                                    element={<Navigate to="/" replace />}
                                />
                                <Route
                                    path="/privacy-policy"
                                    element={
                                        <>
                                            <SEO {...tObject("seo.privacy")} />
                                            <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                <PrivacyPolicy language={language}/>
                                            </div>
                                        </>
                                    }
                                />
                                <Route
                                    path="/terms-of-service"
                                    element={
                                        <>
                                            <SEO 
                                                title={language === "en" ? "Terms of Service - Yurii Suprun" : "Termini di Servizio - Yurii Suprun"}
                                                description={language === "en" ? "Terms of Service and legal conditions for using this website." : "Termini di Servizio e condizioni legali per l'utilizzo di questo sito."}
                                            />
                                            <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                <TermsOfService language={language}/>
                                            </div>
                                        </>
                                    }
                                />
                                {routeConfig(language).map(({path, component, seoKey}) => (
                                    <Route
                                        key={path}
                                        path={path}
                                        element={
                                            <>
                                                <SEO {...tObject(seoKey)} />
                                                <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                    {component}
                                                </div>
                                            </>
                                        }
                                    />
                                ))}
                            </Routes>
                        </main>
                    </>
                ) : (
                    <>
                        {/* Other pages with normal navbar */}
                        <div className="relative z-50 w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                            <Navbar language={language} setLanguage={setLanguage} isHomePage={false}/>
                        </div>
                        <main className="flex-grow text-[16px] sm:text-[17px] leading-[1.65] w-full relative z-10 text-black">
                            <Routes>
                                <Route 
                                    path="/" 
                                    element={
                                        <>
                                            <SEO {...tObject("seo.home")} />
                                            <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                <Hero language={language}/>
                                            </div>
                                        </>
                                    }
                                />
                                <Route 
                                    path="/home" 
                                    element={<Navigate to="/" replace />}
                                />
                                <Route
                                    path="/privacy-policy"
                                    element={
                                        <>
                                            <SEO {...tObject("seo.privacy")} />
                                            <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                <PrivacyPolicy language={language}/>
                                            </div>
                                        </>
                                    }
                                />
                                <Route
                                    path="/terms-of-service"
                                    element={
                                        <>
                                            <SEO 
                                                title={language === "en" ? "Terms of Service - Yurii Suprun" : "Termini di Servizio - Yurii Suprun"}
                                                description={language === "en" ? "Terms of Service and legal conditions for using this website." : "Termini di Servizio e condizioni legali per l'utilizzo di questo sito."}
                                            />
                                            <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                <TermsOfService language={language}/>
                                            </div>
                                        </>
                                    }
                                />
                                {routeConfig(language).map(({path, component, seoKey}) => (
                                    <Route
                                        key={path}
                                        path={path}
                                        element={
                                            <>
                                                <SEO {...tObject(seoKey)} />
                                                <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
                                                    {component}
                                                </div>
                                            </>
                                        }
                                    />
                                ))}
                            </Routes>
                        </main>
                    </>
                )}

                <div className="w-full max-w-6xl mx-auto px-4 sm:px-5 lg:px-8 relative z-10">
                    <Footer language={language}/>
                </div>
                <WhatsAppButton />
                <CookieBanner language={language}/>
            </div>
        </I18nProvider>
    );
}

export default App;
